const APP_ROOT_SELECTOR = '#app-content-root';
const PLACEHOLDER_ATTRIBUTE = 'data-calendly-scroll-placeholder';

let lockedScrollX = 0;
let lockedScrollY = 0;
let isLocked = false;

type AppRootInlineStyles = {
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  maxWidth: string;
  margin: string;
  transform: string;
  zIndex: string;
  boxSizing: string;
};

let previousAppRootStyles: AppRootInlineStyles | null = null;
let placeholderElement: HTMLDivElement | null = null;

const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
]);

const getAppRoot = (): HTMLElement => {
  const element = document.querySelector<HTMLElement>(APP_ROOT_SELECTOR);

  if (!element) {
    throw new Error(`Calendly scroll lock requires ${APP_ROOT_SELECTOR} to exist`);
  }

  return element;
};

/**
 * Returns true when an interaction happened inside Calendly.
 */
const isCalendlyTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest('.calendly-overlay'));
};

/**
 * Prevent wheel scrolling on the application behind Calendly.
 */
const preventBackgroundWheel = (event: WheelEvent): void => {
  if (isCalendlyTarget(event.target)) {
    return;
  }

  event.preventDefault();
};

/**
 * Prevent touch scrolling on the application behind Calendly.
 *
 * Calendly itself remains scrollable.
 */
const preventBackgroundTouchMove = (event: TouchEvent): void => {
  if (isCalendlyTarget(event.target)) {
    return;
  }

  event.preventDefault();
};

/**
 * Prevent keyboard-based background scrolling.
 */
const preventBackgroundKeyboardScroll = (event: KeyboardEvent): void => {
  if (isCalendlyTarget(event.target)) {
    return;
  }

  if (SCROLL_KEYS.has(event.key)) {
    event.preventDefault();
  }
};

/**
 * Prevent focus changes or third-party scripts from moving
 * the document while Calendly is active.
 */
const preserveScrollPosition = (): void => {
  if (!isLocked) {
    return;
  }

  if (window.scrollX === lockedScrollX && window.scrollY === lockedScrollY) {
    return;
  }

  window.scrollTo(lockedScrollX, lockedScrollY);
};

/**
 * Creates invisible document space matching the application's
 * original height.
 *
 * This is important because #app-content-root will temporarily
 * become position: fixed.
 *
 * Without the placeholder, removing the application from normal
 * document flow would reduce the page height and the browser could
 * clamp scrollY, producing the jump you were seeing.
 */
const createPlaceholder = (appRoot: HTMLElement, documentHeight: number): HTMLDivElement => {
  const placeholder = document.createElement('div');

  placeholder.setAttribute(PLACEHOLDER_ATTRIBUTE, 'true');

  placeholder.style.height = `${documentHeight}px`;
  placeholder.style.width = '1px';
  placeholder.style.maxWidth = '1px';
  placeholder.style.pointerEvents = 'none';
  placeholder.style.visibility = 'hidden';
  placeholder.style.overflow = 'hidden';

  appRoot.parentNode?.insertBefore(placeholder, appRoot);

  return placeholder;
};

/**
 * Locks the underlying application without changing html/body
 * viewport behaviour.
 *
 * Important design:
 *
 * html       -> untouched
 * body       -> remains the scrolling document
 *
 * placeholder
 * app root   -> visually frozen
 * Calendly   -> remains independent above the application
 */
export const lockCalendlyPage = (): void => {
  if (typeof window === 'undefined' || isLocked) {
    return;
  }

  const appRoot = getAppRoot();

  /**
   * FIRST:
   * capture everything before modifying layout.
   */
  lockedScrollX = window.scrollX;
  lockedScrollY = window.scrollY;

  const originalRect = appRoot.getBoundingClientRect();

  /**
   * Preserve the complete current document height before making
   * the application fixed.
   */
  const documentHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight,
    appRoot.scrollHeight,
  );

  previousAppRootStyles = {
    position: appRoot.style.position,
    top: appRoot.style.top,
    left: appRoot.style.left,
    right: appRoot.style.right,
    width: appRoot.style.width,
    maxWidth: appRoot.style.maxWidth,
    margin: appRoot.style.margin,
    transform: appRoot.style.transform,
    zIndex: appRoot.style.zIndex,
    boxSizing: appRoot.style.boxSizing,
  };

  /**
   * Keep the document's scrollable height intact.
   *
   * Therefore window.scrollY does NOT collapse when the application
   * is removed from normal flow.
   */
  placeholderElement = createPlaceholder(appRoot, documentHeight);

  /**
   * Freeze ONLY the application.
   *
   * Exact pixel width is intentional.
   *
   * We do NOT use width: 100vw or width: 100% because changing
   * the width calculation could trigger your responsive breakpoints
   * and reproduce the iPhone/iPad layout disturbance.
   */
  appRoot.style.position = 'fixed';
  appRoot.style.top = `${originalRect.top}px`;
  appRoot.style.left = `${originalRect.left}px`;
  appRoot.style.right = 'auto';
  appRoot.style.width = `${originalRect.width}px`;
  appRoot.style.maxWidth = 'none';
  appRoot.style.margin = '0';
  appRoot.style.boxSizing = 'border-box';

  /**
   * Force layout so we can detect how this browser positioned the
   * fixed wrapper.
   *
   * Mobile Safari/Chrome may resolve fixed positioning relative to
   * viewport coordinates differently.
   */
  const fixedRect = appRoot.getBoundingClientRect();

  /**
   * Self-correct any difference BEFORE the browser paints.
   *
   * Example:
   *
   * before fixed:
   * top = -4812
   *
   * after fixed:
   * top = -4770
   *
   * difference = -42
   *
   * We immediately compensate the 42px.
   *
   * This avoids guessing about Safari's visual viewport.
   */
  const topDifference = originalRect.top - fixedRect.top;
  const leftDifference = originalRect.left - fixedRect.left;

  if (topDifference !== 0) {
    const currentTop = Number.parseFloat(appRoot.style.top) || 0;

    appRoot.style.top = `${currentTop + topDifference}px`;
  }

  if (leftDifference !== 0) {
    const currentLeft = Number.parseFloat(appRoot.style.left) || 0;

    appRoot.style.left = `${currentLeft + leftDifference}px`;
  }

  document.documentElement.classList.add('calendly-open');
  document.body.classList.add('calendly-open');

  isLocked = true;

  /**
   * Prevent background interaction/scroll.
   */
  window.addEventListener('wheel', preventBackgroundWheel, {
    passive: false,
  });

  window.addEventListener('touchmove', preventBackgroundTouchMove, {
    passive: false,
  });

  window.addEventListener('keydown', preventBackgroundKeyboardScroll);

  window.addEventListener('scroll', preserveScrollPosition, {
    passive: true,
  });
};

/**
 * Restores the application after Calendly closes.
 */
export const unlockCalendlyPage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  document.documentElement.classList.remove('calendly-open');
  document.body.classList.remove('calendly-open');

  window.removeEventListener('wheel', preventBackgroundWheel);
  window.removeEventListener('touchmove', preventBackgroundTouchMove);
  window.removeEventListener('keydown', preventBackgroundKeyboardScroll);
  window.removeEventListener('scroll', preserveScrollPosition);

  if (!isLocked) {
    return;
  }

  const restoreX = lockedScrollX;
  const restoreY = lockedScrollY;

  const appRoot = document.querySelector<HTMLElement>(APP_ROOT_SELECTOR);

  /**
   * Stop the scroll guard before restoring normal layout.
   */
  isLocked = false;

  if (appRoot && previousAppRootStyles) {
    appRoot.style.position = previousAppRootStyles.position;
    appRoot.style.top = previousAppRootStyles.top;
    appRoot.style.left = previousAppRootStyles.left;
    appRoot.style.right = previousAppRootStyles.right;
    appRoot.style.width = previousAppRootStyles.width;
    appRoot.style.maxWidth = previousAppRootStyles.maxWidth;
    appRoot.style.margin = previousAppRootStyles.margin;
    appRoot.style.transform = previousAppRootStyles.transform;
    appRoot.style.zIndex = previousAppRootStyles.zIndex;
    appRoot.style.boxSizing = previousAppRootStyles.boxSizing;
  }

  /**
   * Remove the temporary document-height placeholder.
   */
  placeholderElement?.remove();

  placeholderElement = null;
  previousAppRootStyles = null;

  lockedScrollX = 0;
  lockedScrollY = 0;

  /**
   * Restore the exact position.
   *
   * This should already be the same position because the placeholder
   * prevented document-height collapse, but this guarantees it.
   */
  window.scrollTo(restoreX, restoreY);
};
